import {
  type OperationsApprovalProposal,
  type OperationsCaseCreate,
  type PlatformIncidentCreate,
} from "@gobro/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, OperationsApi } from "./api";
import type {
  Approval,
  AuditEvent,
  CaseDetail,
  FeatureControl,
  Incident,
  Locale,
  OperationsCase,
  OperationsOrder,
  Operator,
  PasskeyCredential,
  PreviewData,
  Summary,
  Tab,
} from "./domain";
import { money, relativeTime, shortId, shortTime } from "./format";
import { Brand, Icon, type IconName } from "./icons";
import { LiveMap } from "./live-map";
import "./styles.css";

type Phase = "auth" | "boot" | "dashboard" | "mfa" | "otp" | "unauthorized";

interface AppProps {
  api?: OperationsApi;
  previewData?: PreviewData;
}

const EMPTY_SUMMARY: Summary = {
  activeCouriers: 0,
  activeIncidents: 0,
  activeOrders: 0,
  criticalCases: 0,
  openCases: 0,
  pendingApprovals: 0,
  staleOrders: 0,
};

const copy = {
  ar: {
    approvals: "الموافقات",
    audit: "سجل التدقيق",
    cases: "القضايا",
    controls: "التحكم والحوادث",
    live: "التشغيل المباشر",
    offline: "الاتصال منقطع — العرض فقط، جميع الإجراءات معطّلة",
    online: "متصل لحظيًا",
    subtitle: "مركز القيادة المحايد لمنظومة GoBro",
  },
  fr: {
    approvals: "Approbations",
    audit: "Journal d’audit",
    cases: "Dossiers",
    controls: "Contrôles & incidents",
    live: "Exploitation en direct",
    offline: "Hors ligne — lecture seule, toutes les actions sont bloquées",
    online: "Temps réel actif",
    subtitle: "Centre de commande neutre de l’écosystème GoBro",
  },
} as const;

export function App({ api: apiInput, previewData }: AppProps) {
  const api = useMemo(() => apiInput ?? new OperationsApi(), [apiInput]);
  const [phase, setPhase] = useState<Phase>(previewData ? "dashboard" : "boot");
  const [operator, setOperator] = useState<Operator | null>(previewData?.operator ?? null);
  const [locale, setLocale] = useState<Locale>(previewData?.operator.locale ?? "ar");
  const [tab, setTab] = useState<Tab>("live");
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(!previewData);
  const [message, setMessage] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary>(previewData?.summary ?? EMPTY_SUMMARY);
  const [orders, setOrders] = useState<OperationsOrder[]>(previewData?.orders ?? []);
  const [cases, setCases] = useState<OperationsCase[]>(previewData?.cases ?? []);
  const [approvals, setApprovals] = useState<Approval[]>(previewData?.approvals ?? []);
  const [controls, setControls] = useState<FeatureControl[]>(previewData?.controls ?? []);
  const [incidents, setIncidents] = useState<Incident[]>(previewData?.incidents ?? []);
  const [audit, setAudit] = useState<AuditEvent[]>(previewData?.audit ?? []);
  const [search, setSearch] = useState("");
  const [auditQuery, setAuditQuery] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(previewData?.orders[0]?.id ?? null);
  const [perspective, setPerspective] = useState<"courier" | "customer" | "merchant">("customer");
  const [orderDetail, setOrderDetail] = useState<Record<string, unknown> | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState(previewData?.cases[0]?.id ?? null);
  const [caseDetail, setCaseDetail] = useState<CaseDetail | null>(null);
  const [selectedApprovalId, setSelectedApprovalId] = useState(
    previewData?.approvals[0]?.id ?? null,
  );
  const [approvalDetail, setApprovalDetail] = useState<Approval | null>(null);
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [showIncident, setShowIncident] = useState(false);
  const [showPasskeys, setShowPasskeys] = useState(false);
  const [caseNote, setCaseNote] = useState("");
  const [resolutionCode, setResolutionCode] = useState("issue_resolved");
  const [evidenceNote, setEvidenceNote] = useState("");
  const [decisionReason, setDecisionReason] = useState("");
  const [controlReasons, setControlReasons] = useState<Record<string, string>>({});
  const cursor = useRef(0);
  const previewLoaded = useRef(false);
  const isPreview = previewData !== undefined;
  const role = operator?.platformRole ?? operator?.assignedPlatformRole ?? null;
  const canWriteCases = role === "operations_agent" || role === "platform_admin";
  const isAdmin = role === "platform_admin";
  const t = copy[locale];

  const loadOrder = useCallback(
    async (id: string, view = perspective) => {
      setSelectedOrderId(id);
      if (isPreview) {
        const order = orders.find((item) => item.id === id);
        setOrderDetail(order ? previewOrderDetail(order, view) : null);
        return;
      }
      try {
        setOrderDetail(await api.order(id, view));
      } catch (error) {
        setMessage(errorText(error, locale));
      }
    },
    [api, isPreview, locale, orders, perspective],
  );

  const loadCase = useCallback(
    async (id: string) => {
      setSelectedCaseId(id);
      if (isPreview) {
        const item = cases.find((entry) => entry.id === id);
        setCaseDetail(item ? previewCaseDetail(item) : null);
        return;
      }
      try {
        setCaseDetail(await api.case(id));
      } catch (error) {
        setMessage(errorText(error, locale));
      }
    },
    [api, cases, isPreview, locale],
  );

  const loadApproval = useCallback(
    async (id: string) => {
      setSelectedApprovalId(id);
      if (isPreview) {
        setApprovalDetail(approvals.find((entry) => entry.id === id) ?? null);
        return;
      }
      try {
        setApprovalDetail(await api.approval(id));
      } catch (error) {
        setMessage(errorText(error, locale));
      }
    },
    [api, approvals, isPreview, locale],
  );

  const refresh = useCallback(
    async (quiet = false) => {
      if (isPreview || !operator?.mfaVerified || !navigator.onLine) return;
      if (!quiet) setBusy(true);
      try {
        const [
          nextSummary,
          orderPage,
          casePage,
          controlPage,
          incidentPage,
          auditPage,
          approvalPage,
        ] = await Promise.all([
          api.summary(),
          api.orders(search),
          api.cases(search),
          api.controls(),
          api.incidents(),
          api.audit(auditQuery),
          isAdmin ? api.approvals() : Promise.resolve({ items: [] as Approval[] }),
        ]);
        setSummary(nextSummary);
        setOrders(orderPage.items);
        setCases(casePage.items);
        setControls(controlPage.items);
        setIncidents(incidentPage.items);
        setAudit(auditPage.items);
        setApprovals(approvalPage.items);
        setMessage(null);
        const orderId = selectedOrderId ?? orderPage.items[0]?.id;
        const caseId = selectedCaseId ?? casePage.items[0]?.id;
        const approvalId = selectedApprovalId ?? approvalPage.items[0]?.id;
        if (orderId) void loadOrder(orderId);
        if (caseId) void loadCase(caseId);
        if (approvalId && isAdmin) void loadApproval(approvalId);
      } catch (error) {
        setMessage(errorText(error, locale));
      } finally {
        setBusy(false);
      }
    },
    [
      api,
      auditQuery,
      isAdmin,
      isPreview,
      loadApproval,
      loadCase,
      loadOrder,
      locale,
      operator?.mfaVerified,
      search,
      selectedApprovalId,
      selectedCaseId,
      selectedOrderId,
    ],
  );

  const acceptOperator = useCallback((me: Operator) => {
    setOperator(me);
    setLocale(me.locale);
    if (!me.assignedPlatformRole) setPhase("unauthorized");
    else if (!me.mfaVerified) setPhase("mfa");
    else setPhase("dashboard");
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  useEffect(() => {
    if (isPreview) return;
    let active = true;
    void api
      .me()
      .then((me) => active && acceptOperator(me))
      .catch((error) => {
        if (!active) return;
        setPhase(error instanceof ApiError && error.status === 401 ? "auth" : "auth");
        if (!(error instanceof ApiError && error.status === 401))
          setMessage(errorText(error, locale));
      })
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [acceptOperator, api, isPreview, locale]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (phase === "dashboard" && !isPreview) void refreshRef.current();
  }, [isPreview, phase]);

  useEffect(() => {
    if (!isPreview || previewLoaded.current) return;
    previewLoaded.current = true;
    const orderId = previewData.orders[0]?.id;
    const caseId = previewData.cases[0]?.id;
    const approvalId = previewData.approvals[0]?.id;
    if (orderId) void loadOrder(orderId);
    if (caseId) void loadCase(caseId);
    if (approvalId) void loadApproval(approvalId);
  }, [isPreview, loadApproval, loadCase, loadOrder, previewData]);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      void refresh();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refresh]);

  useEffect(() => {
    if (phase !== "dashboard" || isPreview || !online) return;
    const poll = async () => {
      try {
        const result = await api.events(cursor.current);
        cursor.current = result.cursor;
        if (result.events.length) await refreshRef.current(true);
      } catch {
        // The connection status and next successful poll communicate recovery without noisy toasts.
      }
    };
    const timer = window.setInterval(() => void poll(), 4_000);
    void poll();
    return () => window.clearInterval(timer);
  }, [api, isPreview, online, phase]);

  const mutate = async (operation: () => Promise<unknown>) => {
    if (!online || isPreview) {
      setMessage(locale === "ar" ? "الإجراء غير متاح دون اتصال" : "Action indisponible hors ligne");
      return;
    }
    setBusy(true);
    try {
      await operation();
      await refresh();
      setMessage(locale === "ar" ? "تم حفظ الإجراء وتسجيله" : "Action enregistrée et auditée");
    } catch (error) {
      setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };

  if (phase !== "dashboard") {
    return (
      <AuthScreen
        api={api}
        busy={busy}
        locale={locale}
        message={message}
        phase={phase}
        setBusy={setBusy}
        setLocale={setLocale}
        setMessage={setMessage}
        onAuthenticated={acceptOperator}
      />
    );
  }

  const tabs: Array<{ icon: IconName; id: Tab; label: string; visible: boolean }> = [
    { icon: "live", id: "live", label: t.live, visible: true },
    { icon: "cases", id: "cases", label: t.cases, visible: true },
    { icon: "approvals", id: "approvals", label: t.approvals, visible: isAdmin },
    { icon: "controls", id: "controls", label: t.controls, visible: true },
    { icon: "audit", id: "audit", label: t.audit, visible: true },
  ];

  return (
    <div className="ops-shell">
      <aside className="rail">
        <div className="rail-head">
          <Brand />
        </div>
        <nav aria-label={locale === "ar" ? "أقسام التشغيل" : "Sections opérations"}>
          {tabs
            .filter((item) => item.visible)
            .map((item) => (
              <button
                aria-current={tab === item.id ? "page" : undefined}
                className={tab === item.id ? "active" : ""}
                key={item.id}
                onClick={() => setTab(item.id)}
                type="button"
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {item.id === "cases" && summary.criticalCases > 0 && (
                  <em>{summary.criticalCases}</em>
                )}
                {item.id === "approvals" && summary.pendingApprovals > 0 && (
                  <em>{summary.pendingApprovals}</em>
                )}
              </button>
            ))}
        </nav>
        <div className="rail-foot">
          <span className={`connection-dot ${online ? "" : "offline"}`} />
          <div>
            <b>{online ? t.online : "Offline"}</b>
            <small>{roleLabel(role, locale)}</small>
          </div>
        </div>
      </aside>

      <div className="ops-stage">
        {!online && (
          <div className="offline-banner" role="status">
            <Icon name="lock" />
            {t.offline}
          </div>
        )}
        <header className="topbar">
          <div>
            <span className="eyebrow">GOBRO / OPERATIONS</span>
            <h1>{tabs.find((item) => item.id === tab)?.label}</h1>
            <p>{t.subtitle}</p>
          </div>
          <div className="topbar-actions">
            {(tab === "live" || tab === "cases") && (
              <label className="command-search">
                <Icon name="search" />
                <span className="sr-only">{locale === "ar" ? "بحث" : "Rechercher"}</span>
                <input
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && void refresh()}
                  placeholder={
                    locale === "ar" ? "طلب، مطعم، أو قضية…" : "Commande, commerce ou dossier…"
                  }
                  value={search}
                />
                <kbd>↵</kbd>
              </label>
            )}
            <button
              className="icon-button"
              disabled={busy || !online}
              onClick={() => void refresh()}
              title="Refresh"
              type="button"
            >
              <Icon name="refresh" />
            </button>
            <button
              className="locale-button"
              onClick={() => setLocale(locale === "ar" ? "fr" : "ar")}
              type="button"
            >
              {locale === "ar" ? "FR" : "عر"}
            </button>
            <button
              className="operator-avatar"
              onClick={() => setShowPasskeys(true)}
              title={operator?.phoneE164}
              type="button"
            >
              {operator?.phoneE164.slice(-2)}
            </button>
          </div>
        </header>

        {message && (
          <div className="toast" role="status">
            <span>{message}</span>
            <button onClick={() => setMessage(null)} type="button">
              <Icon name="close" />
            </button>
          </div>
        )}
        <main className={busy ? "is-busy" : ""}>
          {tab === "live" && (
            <LiveBoard
              locale={locale}
              online={online && !isPreview}
              onPerspective={(view) => {
                setPerspective(view);
                if (selectedOrderId) void loadOrder(selectedOrderId, view);
              }}
              onSelect={(id) => void loadOrder(id)}
              orderDetail={orderDetail}
              orders={orders}
              perspective={perspective}
              selectedOrderId={selectedOrderId}
              summary={summary}
            />
          )}
          {tab === "cases" && (
            <CasesWorkspace
              canWrite={canWriteCases}
              caseDetail={caseDetail}
              caseNote={caseNote}
              cases={cases}
              evidenceNote={evidenceNote}
              locale={locale}
              online={online && !isPreview}
              resolutionCode={resolutionCode}
              selectedCaseId={selectedCaseId}
              setCaseNote={setCaseNote}
              setEvidenceNote={setEvidenceNote}
              setResolutionCode={setResolutionCode}
              showCreate={showCreateCase}
              onAddEvidence={() => {
                if (!caseDetail || evidenceNote.trim().length < 3) return;
                void mutate(() =>
                  api
                    .addEvidence(caseDetail.id, {
                      evidenceType: "operator_note",
                      note: evidenceNote.trim(),
                      referenceId: null,
                    })
                    .then(() => setEvidenceNote("")),
                );
              }}
              onCreate={(input) =>
                void mutate(() => api.createCase(input).then(() => setShowCreateCase(false)))
              }
              onSelect={(id) => void loadCase(id)}
              onShowCreate={() => setShowCreateCase((value) => !value)}
              onTransition={(status) => {
                if (!caseDetail) return;
                const note = caseNote.trim();
                void mutate(() =>
                  api
                    .updateCase(caseDetail.id, {
                      expectedVersion: caseDetail.version,
                      ...(note ? { note } : {}),
                      ...(status === "resolved" ? { resolutionCode } : {}),
                      status,
                    })
                    .then(() => setCaseNote("")),
                );
              }}
            />
          )}
          {tab === "approvals" && isAdmin && (
            <ApprovalsWorkspace
              approval={approvalDetail}
              approvals={approvals}
              decisionReason={decisionReason}
              locale={locale}
              online={online && !isPreview}
              operatorId={operator?.userId ?? ""}
              setDecisionReason={setDecisionReason}
              showProposal={showProposal}
              onDecide={(action) => {
                if (!approvalDetail || decisionReason.trim().length < 12) return;
                void mutate(() =>
                  api
                    .decideApproval(
                      approvalDetail.id,
                      action,
                      approvalDetail.version,
                      decisionReason.trim(),
                    )
                    .then(() => setDecisionReason("")),
                );
              }}
              onPropose={(input) =>
                void mutate(() => api.proposeApproval(input).then(() => setShowProposal(false)))
              }
              onSelect={(id) => void loadApproval(id)}
              onShowProposal={() => setShowProposal((value) => !value)}
            />
          )}
          {tab === "controls" && (
            <ControlsWorkspace
              controls={controls}
              incidents={incidents}
              isAdmin={isAdmin}
              canManageIncidents={canWriteCases}
              locale={locale}
              online={online && !isPreview}
              reasons={controlReasons}
              setReasons={setControlReasons}
              showIncident={showIncident}
              onCreateIncident={(input) =>
                void mutate(() => api.createIncident(input).then(() => setShowIncident(false)))
              }
              onIncidentStatus={(incident, status) =>
                void mutate(() => api.updateIncident(incident.id, incident.version, status))
              }
              onShowIncident={() => setShowIncident((value) => !value)}
              onToggle={(control) => {
                const reason = controlReasons[control.featureKey]?.trim() ?? "";
                if (reason.length < 12) {
                  setMessage(
                    locale === "ar"
                      ? "اكتب سببًا تشغيليًا واضحًا (12 حرفًا على الأقل)"
                      : "Saisissez une justification claire (12 caractères minimum)",
                  );
                  return;
                }
                void mutate(() => api.updateControl(control, !control.enabled, reason));
              }}
            />
          )}
          {tab === "audit" && (
            <AuditWorkspace
              events={audit}
              locale={locale}
              online={online && !isPreview}
              query={auditQuery}
              setQuery={setAuditQuery}
              onExport={() => void mutate(() => api.downloadAudit(auditQuery))}
              onSearch={() => void refresh()}
            />
          )}
        </main>
      </div>

      <nav className="mobile-nav" aria-label="Mobile operations navigation">
        {tabs
          .filter((item) => item.visible)
          .slice(0, 5)
          .map((item) => (
            <button
              className={tab === item.id ? "active" : ""}
              key={item.id}
              onClick={() => setTab(item.id)}
              type="button"
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
      </nav>

      {showPasskeys && (
        <PasskeysPanel
          api={api}
          locale={locale}
          online={online && !isPreview}
          onClose={() => setShowPasskeys(false)}
          onMessage={setMessage}
        />
      )}
    </div>
  );
}

function AuthScreen({
  api,
  busy,
  locale,
  message,
  onAuthenticated,
  phase,
  setBusy,
  setLocale,
  setMessage,
}: {
  api: OperationsApi;
  busy: boolean;
  locale: Locale;
  message: string | null;
  onAuthenticated: (operator: Operator) => void;
  phase: Phase;
  setBusy: (value: boolean) => void;
  setLocale: (value: Locale) => void;
  setMessage: (value: string | null) => void;
}) {
  const [phone, setPhone] = useState("+216");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [enrolled, setEnrolled] = useState(false);
  const submitOtpRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api.requestOtp(phone);
      setChallengeId(result.challengeId);
      setMessage(null);
    } catch (error) {
      setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };
  const submitOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challengeId) return;
    setBusy(true);
    try {
      await api.verifyOtp(challengeId, code);
      onAuthenticated(await api.me());
    } catch (error) {
      setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };
  const passkey = async () => {
    setBusy(true);
    try {
      await api.verifyPasskey();
      onAuthenticated(await api.me());
    } catch (error) {
      setMessage(passkeyErrorText(error, locale));
    } finally {
      setBusy(false);
    }
  };
  const enroll = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await api.registerPasskey(passkeyDeviceName());
      setEnrolled(true);
    } catch (error) {
      setMessage(passkeyErrorText(error, locale));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth-shell">
      <section className="auth-brand">
        <Brand />
        <div className="auth-copy">
          <span>OPERATIONS CONTROL PLANE</span>
          <h1>
            {locale === "ar"
              ? "رؤية كاملة. قرارات موثّقة."
              : "Vision complète. Décisions traçables."}
          </h1>
          <p>
            {locale === "ar"
              ? "من الطلب حتى التسوية، كل إجراء محمي ومسجّل دون تدخل مباشر في قاعدة البيانات."
              : "De la commande au règlement, chaque action est protégée et auditée, sans accès direct à la base."}
          </p>
        </div>
        <div className="auth-principles">
          <span>
            <Icon name="shield" />
            Passkey + MFA
          </span>
          <span>
            <Icon name="audit" />
            Audit immuable
          </span>
          <span>
            <Icon name="approvals" />
            Maker / Checker
          </span>
        </div>
      </section>
      <section className="auth-panel">
        <button
          className="auth-locale"
          onClick={() => setLocale(locale === "ar" ? "fr" : "ar")}
          type="button"
        >
          {locale === "ar" ? "Français" : "العربية"}
        </button>
        <div className="auth-card">
          {phase === "boot" && (
            <div className="auth-loading">
              <span />
              <p>{locale === "ar" ? "التحقق من جلسة التشغيل…" : "Vérification de la session…"}</p>
            </div>
          )}
          {phase === "unauthorized" && (
            <>
              <Icon className="auth-lock" name="lock" />
              <h2>{locale === "ar" ? "ليس لديك دور تشغيلي" : "Aucun rôle opérationnel"}</h2>
              <p>
                {locale === "ar"
                  ? "اطلب من مسؤول المنصة إسناد دور مناسب وحماية الحساب بمفتاح مرور."
                  : "Demandez à un administrateur d’attribuer un rôle et de protéger le compte par passkey."}
              </p>
              {enrolled ? (
                <p className="auth-success" role="status">
                  {locale === "ar"
                    ? "تم تسجيل مفتاح المرور — اطلب من مسؤول المنصة إسناد الدور الآن."
                    : "Passkey enregistrée — demandez maintenant à un administrateur d’attribuer le rôle."}
                </p>
              ) : (
                <button
                  className="primary-action full"
                  disabled={busy}
                  onClick={() => void enroll()}
                  type="button"
                >
                  <Icon name="shield" />
                  {locale === "ar" ? "تسجيل مفتاح المرور" : "Enregistrer une passkey"}
                </button>
              )}
            </>
          )}
          {phase === "mfa" && (
            <>
              <span className="auth-step">02 / 02</span>
              <Icon className="auth-lock" name="shield" />
              <h2>{locale === "ar" ? "تحقق بمفتاح المرور" : "Vérification par passkey"}</h2>
              <p>
                {locale === "ar"
                  ? "تتطلب لوحة التشغيل حضورًا فعليًا عبر بصمة الجهاز أو مفتاح أمان."
                  : "La console exige une présence physique via biométrie ou clé de sécurité."}
              </p>
              <button
                className="primary-action full"
                disabled={busy}
                onClick={() => void passkey()}
                type="button"
              >
                <Icon name="lock" />
                {locale === "ar" ? "استخدام مفتاح المرور" : "Utiliser la passkey"}
              </button>
            </>
          )}
          {(phase === "auth" || phase === "otp") && (
            <>
              <span className="auth-step">01 / 02</span>
              <h2>{locale === "ar" ? "دخول فريق التشغيل" : "Accès équipe opérations"}</h2>
              <p>
                {locale === "ar"
                  ? "أدخل رقم الهاتف المهني المرتبط بدورك."
                  : "Saisissez le numéro professionnel lié à votre rôle."}
              </p>
              {!challengeId ? (
                <form onSubmit={submitOtpRequest}>
                  <label>
                    {locale === "ar" ? "رقم الهاتف" : "Téléphone"}
                    <input
                      autoComplete="tel"
                      dir="ltr"
                      onChange={(event) => setPhone(event.target.value)}
                      value={phone}
                    />
                  </label>
                  <button
                    className="primary-action full"
                    disabled={busy || phone.length < 8}
                    type="submit"
                  >
                    {locale === "ar" ? "إرسال رمز الدخول" : "Envoyer le code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={submitOtp}>
                  <label>
                    {locale === "ar" ? "الرمز من 6 أرقام" : "Code à 6 chiffres"}
                    <input
                      autoComplete="one-time-code"
                      className="otp-input"
                      dir="ltr"
                      inputMode="numeric"
                      maxLength={6}
                      onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))}
                      value={code}
                    />
                  </label>
                  <button
                    className="primary-action full"
                    disabled={busy || code.length !== 6}
                    type="submit"
                  >
                    {locale === "ar" ? "متابعة آمنة" : "Continuer en sécurité"}
                  </button>
                  <button
                    className="text-button"
                    onClick={() => setChallengeId(null)}
                    type="button"
                  >
                    {locale === "ar" ? "تغيير الرقم" : "Changer le numéro"}
                  </button>
                </form>
              )}
            </>
          )}
          {message && (
            <div className="auth-error" role="alert">
              {message}
            </div>
          )}
        </div>
        <small className="auth-legal">GoBro Security Boundary · Session events are audited</small>
      </section>
    </main>
  );
}

function PasskeysPanel({
  api,
  locale,
  online,
  onClose,
  onMessage,
}: {
  api: OperationsApi;
  locale: Locale;
  online: boolean;
  onClose: () => void;
  onMessage: (value: string | null) => void;
}) {
  const [passkeys, setPasskeys] = useState<PasskeyCredential[] | null>(null);
  const [busyAction, setBusyAction] = useState(false);

  const load = useCallback(async () => {
    try {
      setPasskeys(await api.passkeys());
    } catch (error) {
      onMessage(errorText(error, locale));
      onClose();
    }
  }, [api, locale, onClose, onMessage]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (operation: () => Promise<unknown>, success: string) => {
    setBusyAction(true);
    try {
      await operation();
      await load();
      onMessage(success);
    } catch (error) {
      onMessage(passkeyErrorText(error, locale));
    } finally {
      setBusyAction(false);
    }
  };

  const active = (passkeys ?? []).filter((item) => item.revokedAt === null);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: backdrop dismissal mirrors the close control inside the dialog
    <div
      className="passkey-backdrop"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        aria-label={locale === "ar" ? "إدارة مفاتيح المرور" : "Gestion des passkeys"}
        aria-modal="true"
        className="passkey-panel"
        role="dialog"
      >
        <header>
          <h2>{locale === "ar" ? "مفاتيح المرور" : "Passkeys"}</h2>
          <button
            aria-label={locale === "ar" ? "إغلاق" : "Fermer"}
            className="icon-button"
            onClick={onClose}
            type="button"
          >
            <Icon name="close" />
          </button>
        </header>
        <p>
          {locale === "ar"
            ? "مفاتيح المرور تثبت حضورك الفعلي عند دخول لوحة التشغيل."
            : "Les passkeys prouvent votre présence physique à l’ouverture de la console."}
        </p>
        {passkeys === null ? (
          <p>{locale === "ar" ? "جار التحميل…" : "Chargement…"}</p>
        ) : active.length === 0 ? (
          <p>{locale === "ar" ? "لا توجد مفاتيح مرور نشطة." : "Aucune passkey active."}</p>
        ) : (
          <ul className="passkey-list">
            {active.map((item) => (
              <li className="passkey-item" key={item.id}>
                <div>
                  <b>{item.name}</b>
                  <small>
                    {locale === "ar" ? "آخر استخدام: " : "Dernier usage : "}
                    {item.lastUsedAt ? relativeTime(item.lastUsedAt, locale) : "—"}
                  </small>
                </div>
                <button
                  className="reject-action"
                  disabled={busyAction || !online}
                  onClick={() =>
                    void act(
                      () => api.revokePasskey(item.id),
                      locale === "ar" ? "تم حذف مفتاح المرور" : "Passkey révoquée",
                    )
                  }
                  type="button"
                >
                  {locale === "ar" ? "حذف" : "Révoquer"}
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          className="primary-action full"
          disabled={busyAction || !online}
          onClick={() =>
            void act(
              () => api.registerPasskey(passkeyDeviceName()),
              locale === "ar" ? "تم تسجيل مفتاح مرور جديد" : "Nouvelle passkey enregistrée",
            )
          }
          type="button"
        >
          <Icon name="plus" />
          {locale === "ar" ? "إضافة مفتاح مرور" : "Ajouter une passkey"}
        </button>
      </section>
    </div>
  );
}

function LiveBoard({
  locale,
  online,
  onPerspective,
  onSelect,
  orderDetail,
  orders,
  perspective,
  selectedOrderId,
  summary,
}: {
  locale: Locale;
  online: boolean;
  onPerspective: (view: "courier" | "customer" | "merchant") => void;
  onSelect: (id: string) => void;
  orderDetail: Record<string, unknown> | null;
  orders: OperationsOrder[];
  perspective: "courier" | "customer" | "merchant";
  selectedOrderId: string | null;
  summary: Summary;
}) {
  const metrics = [
    [locale === "ar" ? "طلبات نشطة" : "Commandes actives", summary.activeOrders, "neutral"],
    [
      locale === "ar" ? "متأخرة تشغيليًا" : "En retard",
      summary.staleOrders,
      summary.staleOrders ? "warning" : "neutral",
    ],
    [locale === "ar" ? "سائقون متصلون" : "Coursiers en ligne", summary.activeCouriers, "positive"],
    [
      locale === "ar" ? "قضايا حرجة" : "Dossiers critiques",
      summary.criticalCases,
      summary.criticalCases ? "danger" : "neutral",
    ],
  ] as const;
  return (
    <>
      <section className="metric-strip" aria-label="Operational summary">
        {metrics.map(([label, value, tone]) => (
          <div key={label}>
            <span className={`metric-signal ${tone}`} />
            <small>{label}</small>
            <strong>{value.toLocaleString(locale)}</strong>
          </div>
        ))}
        <span className="metric-updated">
          <span className="pulse" />
          {locale === "ar" ? "آخر تحديث الآن" : "Actualisé maintenant"}
        </span>
      </section>
      <section className="live-grid">
        <div className="order-board surface">
          <div className="section-head">
            <div>
              <span className="section-kicker">LIVE QUEUE</span>
              <h2>{locale === "ar" ? "تدفّق الطلبات" : "Flux des commandes"}</h2>
            </div>
            <div className="legend">
              <span className="dot green" />
              {locale === "ar" ? "طبيعي" : "Normal"}
              <span className="dot amber" />
              {locale === "ar" ? "يحتاج انتباه" : "Attention"}
            </div>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{locale === "ar" ? "الطلب" : "Commande"}</th>
                  <th>{locale === "ar" ? "المطعم" : "Commerce"}</th>
                  <th>{locale === "ar" ? "الحالة" : "État"}</th>
                  <th>{locale === "ar" ? "السائق" : "Coursier"}</th>
                  <th>{locale === "ar" ? "القيمة" : "Valeur"}</th>
                  <th>{locale === "ar" ? "العمر" : "Âge"}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    className={selectedOrderId === order.id ? "selected" : ""}
                    key={order.id}
                    onClick={() => onSelect(order.id)}
                  >
                    <td>
                      <button onClick={() => onSelect(order.id)} type="button">
                        <b>#{order.publicReference}</b>
                        <small>{shortId(order.id)}</small>
                      </button>
                    </td>
                    <td>{order.storeName}</td>
                    <td>
                      <Status value={order.status} />
                    </td>
                    <td>
                      {order.courierName ?? (
                        <span className="muted">
                          {locale === "ar" ? "دون تعيين" : "Non assigné"}
                        </span>
                      )}
                    </td>
                    <td>{money(order.customerTotalMillimes, locale)}</td>
                    <td>{relativeTime(order.createdAt, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && (
              <Empty
                label={locale === "ar" ? "لا توجد طلبات مطابقة" : "Aucune commande correspondante"}
              />
            )}
          </div>
        </div>
        <div className="live-side">
          <LiveMap
            locale={locale}
            online={online}
            onSelect={onSelect}
            orderDetail={orderDetail}
            orders={orders}
            selectedId={selectedOrderId}
          />
          <section className="inspector surface">
            <div className="section-head compact">
              <div>
                <span className="section-kicker">ROLE VIEW</span>
                <h2>{locale === "ar" ? "منظور الأطراف" : "Vue par rôle"}</h2>
              </div>
              <span className="secure-label">
                <Icon name="shield" />
                RLS
              </span>
            </div>
            <div className="segmented">
              {(["customer", "merchant", "courier"] as const).map((view) => (
                <button
                  className={perspective === view ? "active" : ""}
                  key={view}
                  onClick={() => onPerspective(view)}
                  type="button"
                >
                  {view === "customer"
                    ? locale === "ar"
                      ? "الزبون"
                      : "Client"
                    : view === "merchant"
                      ? locale === "ar"
                        ? "المطعم"
                        : "Commerce"
                      : locale === "ar"
                        ? "السائق"
                        : "Coursier"}
                </button>
              ))}
            </div>
            {orderDetail ? (
              <DetailTree data={orderDetail} locale={locale} />
            ) : (
              <Empty
                label={locale === "ar" ? "اختر طلبًا لعرض تفاصيله" : "Sélectionnez une commande"}
              />
            )}
          </section>
        </div>
      </section>
    </>
  );
}

function CasesWorkspace(props: {
  canWrite: boolean;
  caseDetail: CaseDetail | null;
  caseNote: string;
  cases: OperationsCase[];
  evidenceNote: string;
  locale: Locale;
  online: boolean;
  resolutionCode: string;
  selectedCaseId: string | null;
  setCaseNote: (value: string) => void;
  setEvidenceNote: (value: string) => void;
  setResolutionCode: (value: string) => void;
  showCreate: boolean;
  onAddEvidence: () => void;
  onCreate: (input: OperationsCaseCreate) => void;
  onSelect: (id: string) => void;
  onShowCreate: () => void;
  onTransition: (status: "investigating" | "resolved") => void;
}) {
  const { locale } = props;
  return (
    <section className="workspace-grid">
      <div className="queue-panel surface">
        <div className="section-head">
          <div>
            <span className="section-kicker">DISPUTE QUEUE</span>
            <h2>{locale === "ar" ? "صندوق القضايا" : "File des dossiers"}</h2>
          </div>
          {props.canWrite && (
            <button
              className="secondary-action"
              disabled={!props.online}
              onClick={props.onShowCreate}
              type="button"
            >
              <Icon name="plus" />
              {locale === "ar" ? "قضية جديدة" : "Nouveau"}
            </button>
          )}
        </div>
        {props.showCreate && <CaseCreateForm locale={locale} onSubmit={props.onCreate} />}
        <div className="case-list">
          {props.cases.map((item) => (
            <button
              className={props.selectedCaseId === item.id ? "active" : ""}
              key={item.id}
              onClick={() => props.onSelect(item.id)}
              type="button"
            >
              <span className={`priority-line ${item.priority}`} />
              <div>
                <b>{item.title}</b>
                <small>
                  {item.publicReference} · {relativeTime(item.updatedAt, locale)}
                </small>
              </div>
              <div>
                <Status value={item.status} />
                <small>{item.category.replaceAll("_", " ")}</small>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="case-detail surface">
        {props.caseDetail ? (
          <>
            <div className="case-title">
              <div>
                <span>{props.caseDetail.publicReference}</span>
                <h2>{props.caseDetail.title}</h2>
                <p>{props.caseDetail.summary}</p>
              </div>
              <div>
                <Status value={props.caseDetail.status} />
                <strong className={`priority-text ${props.caseDetail.priority}`}>
                  {props.caseDetail.priority}
                </strong>
              </div>
            </div>
            <div className="case-meta">
              <span>
                {locale === "ar" ? "الطلب" : "Commande"}
                <b>{shortId(props.caseDetail.orderId)}</b>
              </span>
              <span>
                {locale === "ar" ? "المكلّف" : "Assigné"}
                <b>{shortId(props.caseDetail.assignedToUserId)}</b>
              </span>
              <span>
                {locale === "ar" ? "النسخة" : "Version"}
                <b>v{props.caseDetail.version}</b>
              </span>
            </div>
            {props.canWrite && props.caseDetail.status !== "closed" && (
              <div className="case-actions">
                <textarea
                  onChange={(event) => props.setCaseNote(event.target.value)}
                  placeholder={
                    locale === "ar" ? "ملاحظة القرار (تُشفّر وتُحفظ)…" : "Note de décision (chiffrée)…"
                  }
                  value={props.caseNote}
                />
                <div className="inline-fields">
                  <input
                    onChange={(event) => props.setResolutionCode(event.target.value)}
                    value={props.resolutionCode}
                  />
                  <button
                    className="secondary-action"
                    disabled={!props.online}
                    onClick={() => props.onTransition("investigating")}
                    type="button"
                  >
                    {locale === "ar" ? "بدء التحقيق" : "Enquêter"}
                  </button>
                  <button
                    className="primary-action"
                    disabled={!props.online || props.caseNote.trim().length < 3}
                    onClick={() => props.onTransition("resolved")}
                    type="button"
                  >
                    <Icon name="check" />
                    {locale === "ar" ? "حل القضية" : "Résoudre"}
                  </button>
                </div>
              </div>
            )}
            <div className="case-columns">
              <div>
                <h3>{locale === "ar" ? "الخط الزمني" : "Chronologie"}</h3>
                <ol className="timeline">
                  {props.caseDetail.events.map((event) => (
                    <li key={event.id}>
                      <span />
                      <div>
                        <b>{event.eventType.replaceAll("_", " ")}</b>
                        <p>{event.note ?? event.toStatus}</p>
                        <small>
                          {shortTime(event.createdAt, locale)} · {shortId(event.performedByUserId)}
                        </small>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div>
                <h3>{locale === "ar" ? "الأدلة" : "Éléments probants"}</h3>
                <div className="evidence-list">
                  {props.caseDetail.evidence.map((item) => (
                    <article key={item.id}>
                      <Icon name="shield" />
                      <div>
                        <b>{item.evidenceType.replaceAll("_", " ")}</b>
                        <p>{item.note}</p>
                        <small>SHA · {item.evidenceHash.slice(0, 12)}…</small>
                      </div>
                    </article>
                  ))}
                </div>
                {props.canWrite && (
                  <div className="evidence-add">
                    <textarea
                      onChange={(event) => props.setEvidenceNote(event.target.value)}
                      placeholder={
                        locale === "ar"
                          ? "أضف دليلًا أو ملاحظة غير قابلة للتعديل…"
                          : "Ajouter une preuve immuable…"
                      }
                      value={props.evidenceNote}
                    />
                    <button
                      className="secondary-action"
                      disabled={!props.online || props.evidenceNote.trim().length < 3}
                      onClick={props.onAddEvidence}
                      type="button"
                    >
                      <Icon name="plus" />
                      {locale === "ar" ? "إضافة دليل" : "Ajouter"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <Empty label={locale === "ar" ? "اختر قضية" : "Sélectionnez un dossier"} />
        )}
      </div>
    </section>
  );
}

function CaseCreateForm({
  locale,
  onSubmit,
}: {
  locale: Locale;
  onSubmit: (input: OperationsCaseCreate) => void;
}) {
  return (
    <form
      className="inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        onSubmit({
          assignedToUserId: null,
          category: data.get("category") as OperationsCaseCreate["category"],
          orderId: String(data.get("orderId")),
          priority: data.get("priority") as OperationsCaseCreate["priority"],
          summary: String(data.get("summary")),
          supportRequestId: null,
          title: String(data.get("title")),
        });
      }}
    >
      <input
        name="orderId"
        pattern="[0-9a-f-]{36}"
        placeholder={locale === "ar" ? "معرّف الطلب UUID" : "UUID commande"}
        required
      />
      <input
        minLength={4}
        name="title"
        placeholder={locale === "ar" ? "عنوان واضح" : "Titre clair"}
        required
      />
      <select defaultValue="delivery_issue" name="category">
        <option value="delivery_issue">Delivery</option>
        <option value="payment_issue">Payment</option>
        <option value="quality_issue">Quality</option>
        <option value="safety_incident">Safety</option>
        <option value="fraud_review">Fraud</option>
      </select>
      <select defaultValue="normal" name="priority">
        <option value="normal">Normal</option>
        <option value="high">High</option>
        <option value="critical">Critical</option>
        <option value="low">Low</option>
      </select>
      <textarea
        minLength={12}
        name="summary"
        placeholder={locale === "ar" ? "ملخص القضية والسياق…" : "Résumé et contexte…"}
        required
      />
      <button className="primary-action" type="submit">
        <Icon name="plus" />
        {locale === "ar" ? "فتح القضية" : "Ouvrir"}
      </button>
    </form>
  );
}

function ApprovalsWorkspace(props: {
  approval: Approval | null;
  approvals: Approval[];
  decisionReason: string;
  locale: Locale;
  online: boolean;
  operatorId: string;
  setDecisionReason: (value: string) => void;
  showProposal: boolean;
  onDecide: (action: "approve" | "reject" | "retry") => void;
  onPropose: (input: OperationsApprovalProposal) => void;
  onSelect: (id: string) => void;
  onShowProposal: () => void;
}) {
  const makerConflict = props.approval?.makerUserId === props.operatorId;
  return (
    <section className="workspace-grid approvals-workspace">
      <div className="queue-panel surface">
        <div className="section-head">
          <div>
            <span className="section-kicker">MAKER / CHECKER</span>
            <h2>{props.locale === "ar" ? "طابور القرارات المالية" : "Décisions financières"}</h2>
          </div>
          <button
            className="secondary-action"
            disabled={!props.online}
            onClick={props.onShowProposal}
            type="button"
          >
            <Icon name="plus" />
            {props.locale === "ar" ? "اقتراح" : "Proposer"}
          </button>
        </div>
        {props.showProposal && (
          <ApprovalProposalForm locale={props.locale} onSubmit={props.onPropose} />
        )}
        <div className="approval-list">
          {props.approvals.map((item) => (
            <button
              className={props.approval?.id === item.id ? "active" : ""}
              key={item.id}
              onClick={() => props.onSelect(item.id)}
              type="button"
            >
              <span className="approval-icon">
                <Icon name={item.actionType === "full_refund" ? "refresh" : "controls"} />
              </span>
              <div>
                <b>
                  {item.actionType === "full_refund"
                    ? props.locale === "ar"
                      ? "استرجاع كامل"
                      : "Remboursement"
                    : props.locale === "ar"
                      ? "سياسة رسوم"
                      : "Politique tarifaire"}
                </b>
                <small>
                  {shortId(item.id)} · {relativeTime(item.createdAt, props.locale)}
                </small>
              </div>
              <Status value={item.status} />
            </button>
          ))}
        </div>
      </div>
      <div className="approval-detail surface">
        {props.approval ? (
          <>
            <div className="approval-hero">
              <span className="approval-icon large">
                <Icon name={props.approval.actionType === "full_refund" ? "refresh" : "controls"} />
              </span>
              <div>
                <span className="section-kicker">HIGH-RISK ACTION</span>
                <h2>{props.approval.actionType.replaceAll("_", " ")}</h2>
                <p>
                  {props.approval.justification ??
                    (props.locale === "ar"
                      ? "مبرر الطلب متاح بعد فتح التفاصيل الآمنة."
                      : "La justification est disponible dans le détail sécurisé.")}
                </p>
              </div>
              <Status value={props.approval.status} />
            </div>
            <div className="maker-checker">
              <div>
                <small>MAKER</small>
                <b>{shortId(props.approval.makerUserId)}</b>
                <span>
                  {props.approval.makerUserId === props.operatorId
                    ? props.locale === "ar"
                      ? "أنت"
                      : "Vous"
                    : "✓"}
                </span>
              </div>
              <i />
              <div>
                <small>CHECKER</small>
                <b>{shortId(props.approval.checkerUserId)}</b>
                <span>{props.approval.checkerUserId ? "✓" : "02"}</span>
              </div>
            </div>
            <div className="payload">
              <h3>{props.locale === "ar" ? "الأثر المقترح" : "Impact proposé"}</h3>
              <DetailTree data={props.approval.payload} locale={props.locale} />
            </div>
            {props.approval.status === "pending" && (
              <div className="decision-box">
                <label>
                  {props.locale === "ar" ? "مبرر قرار المراجع" : "Motif du contrôleur"}
                  <textarea
                    minLength={12}
                    onChange={(event) => props.setDecisionReason(event.target.value)}
                    placeholder={
                      props.locale === "ar"
                        ? "اكتب سببًا مستقلاً ومحددًا…"
                        : "Motif indépendant et précis…"
                    }
                    value={props.decisionReason}
                  />
                </label>
                {makerConflict && (
                  <p className="separation-warning">
                    <Icon name="lock" />
                    {props.locale === "ar"
                      ? "لا يمكنك مراجعة اقتراح أنشأته. يلزم مدير ثانٍ."
                      : "Vous ne pouvez pas contrôler votre propre proposition."}
                  </p>
                )}
                <div>
                  <button
                    className="reject-action"
                    disabled={
                      !props.online || makerConflict || props.decisionReason.trim().length < 12
                    }
                    onClick={() => props.onDecide("reject")}
                    type="button"
                  >
                    {props.locale === "ar" ? "رفض موثّق" : "Rejeter"}
                  </button>
                  <button
                    className="primary-action"
                    disabled={
                      !props.online || makerConflict || props.decisionReason.trim().length < 12
                    }
                    onClick={() => props.onDecide("approve")}
                    type="button"
                  >
                    <Icon name="check" />
                    {props.locale === "ar" ? "موافقة وتنفيذ" : "Approuver & exécuter"}
                  </button>
                </div>
              </div>
            )}
            {props.approval.status === "execution_failed" && (
              <div className="decision-box">
                <textarea
                  minLength={12}
                  onChange={(event) => props.setDecisionReason(event.target.value)}
                  placeholder={
                    props.locale === "ar" ? "سبب إعادة المحاولة…" : "Motif de nouvelle tentative…"
                  }
                  value={props.decisionReason}
                />
                <button
                  className="primary-action"
                  disabled={
                    !props.online || makerConflict || props.decisionReason.trim().length < 12
                  }
                  onClick={() => props.onDecide("retry")}
                  type="button"
                >
                  <Icon name="refresh" />
                  {props.locale === "ar" ? "إعادة تنفيذ آمنة" : "Réexécuter"}
                </button>
              </div>
            )}
          </>
        ) : (
          <Empty label={props.locale === "ar" ? "اختر موافقة" : "Sélectionnez une approbation"} />
        )}
      </div>
    </section>
  );
}

function ApprovalProposalForm({
  locale,
  onSubmit,
}: {
  locale: Locale;
  onSubmit: (input: OperationsApprovalProposal) => void;
}) {
  const [type, setType] = useState<"fee_policy_activation" | "full_refund">("full_refund");
  return (
    <form
      className="inline-form approval-form"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const justification = String(data.get("justification"));
        const raw =
          type === "full_refund"
            ? {
                actionType: type,
                expectedOrderVersion: Number(data.get("version")),
                justification,
                orderId: String(data.get("orderId")),
                reason: "delivery_failure",
              }
            : {
                actionType: type,
                courierFeeMillimes: Number(data.get("courierFee")),
                justification,
                merchantFeeMillimes: Number(data.get("merchantFee")),
                scopeKey: "global",
                waiverBasisPoints: Number(data.get("waiver")),
              };
        onSubmit(raw as OperationsApprovalProposal);
      }}
    >
      <select onChange={(event) => setType(event.target.value as typeof type)} value={type}>
        <option value="full_refund">Full refund</option>
        <option value="fee_policy_activation">Fee policy</option>
      </select>
      {type === "full_refund" ? (
        <>
          <input name="orderId" pattern="[0-9a-f-]{36}" placeholder="Order UUID" required />
          <input min="1" name="version" placeholder="Order version" required type="number" />
        </>
      ) : (
        <>
          <input
            min="0"
            name="merchantFee"
            placeholder="Merchant millimes"
            required
            type="number"
          />
          <input min="0" name="courierFee" placeholder="Courier millimes" required type="number" />
          <input
            max="10000"
            min="0"
            name="waiver"
            placeholder="Waiver BPS"
            required
            type="number"
          />
        </>
      )}
      <textarea
        minLength={12}
        name="justification"
        placeholder={
          locale === "ar"
            ? "مبرر الاقتراح (12 حرفًا على الأقل)…"
            : "Justification (12 caractères minimum)…"
        }
        required
      />
      <button className="primary-action" type="submit">
        <Icon name="approvals" />
        {locale === "ar" ? "إرسال للمراجعة" : "Soumettre"}
      </button>
    </form>
  );
}

function ControlsWorkspace(props: {
  controls: FeatureControl[];
  incidents: Incident[];
  isAdmin: boolean;
  canManageIncidents: boolean;
  locale: Locale;
  online: boolean;
  reasons: Record<string, string>;
  setReasons: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  showIncident: boolean;
  onCreateIncident: (input: PlatformIncidentCreate) => void;
  onIncidentStatus: (incident: Incident, status: "active" | "resolved") => void;
  onShowIncident: () => void;
  onToggle: (control: FeatureControl) => void;
}) {
  return (
    <div className="controls-layout">
      <section className="surface control-panel">
        <div className="section-head">
          <div>
            <span className="section-kicker">FEATURE KILL SWITCHES</span>
            <h2>{props.locale === "ar" ? "مفاتيح السلامة" : "Interrupteurs de sécurité"}</h2>
          </div>
          <span className="secure-label">
            <Icon name="lock" />
            ADMIN ONLY
          </span>
        </div>
        <p className="section-note">
          {props.locale === "ar"
            ? "الإيقاف يمنع العمليات الجديدة فقط؛ الإلغاء والرفض وإنهاء الرحلات الجارية تبقى متاحة."
            : "L’arrêt bloque les nouvelles opérations; les sorties sûres restent disponibles."}
        </p>
        <div className="control-list">
          {props.controls.map((control) => (
            <article key={control.featureKey}>
              <div>
                <span className={`control-state ${control.enabled ? "on" : "off"}`} />
                <div>
                  <b>{featureLabel(control.featureKey, props.locale)}</b>
                  <small>
                    {control.reason} · v{control.version}
                  </small>
                </div>
              </div>
              <div className="control-action">
                <input
                  aria-label={`${control.featureKey} reason`}
                  disabled={!props.isAdmin}
                  onChange={(event) =>
                    props.setReasons((current) => ({
                      ...current,
                      [control.featureKey]: event.target.value,
                    }))
                  }
                  placeholder={
                    props.locale === "ar" ? "سبب التغيير المطلوب…" : "Motif obligatoire…"
                  }
                  value={props.reasons[control.featureKey] ?? ""}
                />
                <button
                  aria-label={`${control.featureKey} toggle`}
                  className={`switch ${control.enabled ? "on" : ""}`}
                  disabled={!props.isAdmin || !props.online}
                  onClick={() => props.onToggle(control)}
                  type="button"
                >
                  <span />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="surface incident-panel">
        <div className="section-head">
          <div>
            <span className="section-kicker">INCIDENT COMMAND</span>
            <h2>{props.locale === "ar" ? "بلاغات المنصة" : "Incidents plateforme"}</h2>
          </div>
          {props.canManageIncidents && (
            <button
              className="secondary-action"
              disabled={!props.online}
              onClick={props.onShowIncident}
              type="button"
            >
              <Icon name="plus" />
              {props.locale === "ar" ? "بلاغ" : "Incident"}
            </button>
          )}
        </div>
        {props.showIncident && (
          <IncidentForm locale={props.locale} onSubmit={props.onCreateIncident} />
        )}
        <div className="incident-list">
          {props.incidents.map((incident) => (
            <article className={incident.severity} key={incident.id}>
              <div className="incident-severity">
                <span />
                <small>{incident.severity}</small>
              </div>
              <div>
                <div className="incident-title">
                  <b>{props.locale === "ar" ? incident.titleAr : incident.titleFr}</b>
                  <Status value={incident.status} />
                </div>
                <p>{props.locale === "ar" ? incident.messageAr : incident.messageFr}</p>
                <small>
                  {incident.audiences.join(" · ")} ·{" "}
                  {relativeTime(incident.updatedAt, props.locale)}
                </small>
                {props.canManageIncidents && incident.status !== "resolved" && (
                  <div className="incident-actions">
                    {incident.status === "draft" && (
                      <button
                        disabled={!props.online}
                        onClick={() => props.onIncidentStatus(incident, "active")}
                        type="button"
                      >
                        {props.locale === "ar" ? "نشر البلاغ" : "Activer"}
                      </button>
                    )}
                    {incident.status === "active" && (
                      <button
                        disabled={!props.online}
                        onClick={() => props.onIncidentStatus(incident, "resolved")}
                        type="button"
                      >
                        {props.locale === "ar" ? "إعلان الحل" : "Résoudre"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function IncidentForm({
  locale,
  onSubmit,
}: {
  locale: Locale;
  onSubmit: (input: PlatformIncidentCreate) => void;
}) {
  return (
    <form
      className="inline-form incident-form"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        onSubmit({
          audiences: ["customer", "merchant", "courier", "operations"],
          messageAr: String(data.get("messageAr")),
          messageFr: String(data.get("messageFr")),
          severity: data.get("severity") as "critical" | "info" | "warning",
          status: "draft",
          titleAr: String(data.get("titleAr")),
          titleFr: String(data.get("titleFr")),
        });
      }}
    >
      <select defaultValue="warning" name="severity">
        <option value="info">Info</option>
        <option value="warning">Warning</option>
        <option value="critical">Critical</option>
      </select>
      <input minLength={4} name="titleAr" placeholder="العنوان بالعربية" required />
      <input minLength={4} name="titleFr" placeholder="Titre français" required />
      <textarea minLength={8} name="messageAr" placeholder="رسالة البلاغ بالعربية" required />
      <textarea minLength={8} name="messageFr" placeholder="Message de l’incident" required />
      <button className="primary-action" type="submit">
        <Icon name="plus" />
        {locale === "ar" ? "حفظ كمسودة" : "Enregistrer"}
      </button>
    </form>
  );
}

function AuditWorkspace({
  events,
  locale,
  online,
  onExport,
  onSearch,
  query,
  setQuery,
}: {
  events: AuditEvent[];
  locale: Locale;
  online: boolean;
  onExport: () => void;
  onSearch: () => void;
  query: string;
  setQuery: (value: string) => void;
}) {
  return (
    <section className="audit-panel surface">
      <div className="section-head">
        <div>
          <span className="section-kicker">IMMUTABLE AUDIT TRAIL</span>
          <h2>{locale === "ar" ? "بحث وتصدير الأحداث" : "Recherche et export"}</h2>
        </div>
        <button className="secondary-action" disabled={!online} onClick={onExport} type="button">
          <Icon name="download" />
          CSV
        </button>
      </div>
      <div className="audit-filters">
        <label>
          <Icon name="search" />
          <input
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && onSearch()}
            placeholder={locale === "ar" ? "صفِّ حسب الإجراء…" : "Filtrer par action…"}
            value={query}
          />
        </label>
        <button className="primary-action" disabled={!online} onClick={onSearch} type="button">
          {locale === "ar" ? "تطبيق" : "Appliquer"}
        </button>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>{locale === "ar" ? "الوقت" : "Heure"}</th>
              <th>{locale === "ar" ? "الإجراء" : "Action"}</th>
              <th>{locale === "ar" ? "المنفّذ" : "Acteur"}</th>
              <th>{locale === "ar" ? "الهدف" : "Cible"}</th>
              <th>Request ID</th>
              <th>{locale === "ar" ? "البيانات الآمنة" : "Métadonnées"}</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td>
                  {shortTime(event.createdAt, locale)}
                  <small>{new Date(event.createdAt).toLocaleDateString(locale)}</small>
                </td>
                <td>
                  <code>{event.action}</code>
                </td>
                <td>{shortId(event.actorUserId)}</td>
                <td>
                  {event.targetType}
                  <small>{shortId(event.targetId)}</small>
                </td>
                <td>
                  <code>{shortId(event.requestId)}</code>
                </td>
                <td>
                  <span className="metadata-count">{Object.keys(event.metadata).length}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DetailTree({ data, locale }: { data: Record<string, unknown>; locale: Locale }) {
  const entries = Object.entries(data).filter(
    ([, value]) => value !== null && value !== undefined && typeof value !== "object",
  );
  const nested = Object.entries(data).filter(
    ([, value]) => value !== null && typeof value === "object",
  );
  return (
    <div className="detail-tree">
      <dl>
        {entries.slice(0, 12).map(([key, value]) => (
          <div key={key}>
            <dt>{humanKey(key)}</dt>
            <dd>{formatDetailValue(key, value, locale)}</dd>
          </div>
        ))}
      </dl>
      {nested.slice(0, 5).map(([key, value]) => (
        <section key={key}>
          <h3>{humanKey(key)}</h3>
          {Array.isArray(value) ? (
            <div className="nested-list">
              {value.slice(0, 8).map((item) => (
                <span key={`${key}-${detailItemKey(item)}`}>
                  {typeof item === "object" ? JSON.stringify(item) : String(item)}
                </span>
              ))}
            </div>
          ) : (
            <DetailTree data={value as Record<string, unknown>} locale={locale} />
          )}
        </section>
      ))}
    </div>
  );
}

function detailItemKey(item: unknown): string {
  return typeof item === "object" && item !== null ? JSON.stringify(item) : String(item);
}

function Status({ value }: { value: string }) {
  return (
    <span className={`status status-${value.replaceAll("_", "-")}`}>
      {value.replaceAll("_", " ")}
    </span>
  );
}
function Empty({ label }: { label: string }) {
  return (
    <div className="empty">
      <Icon name="search" />
      <p>{label}</p>
    </div>
  );
}

function humanKey(key: string) {
  return key
    .replace(/([A-Z])/gu, " $1")
    .replaceAll("_", " ")
    .trim();
}
function formatDetailValue(key: string, value: unknown, locale: Locale) {
  if (typeof value === "number" && key.toLowerCase().includes("millimes"))
    return money(value, locale);
  if (typeof value === "boolean") return value ? "✓" : "—";
  return String(value);
}
function roleLabel(role: string | null, locale: Locale) {
  if (role === "platform_admin") return locale === "ar" ? "مدير المنصة" : "Administrateur";
  if (role === "operations_agent") return locale === "ar" ? "مشغّل المنصة" : "Opérateur";
  return locale === "ar" ? "دعم — قراءة فقط" : "Support — lecture seule";
}
function featureLabel(key: FeatureControl["featureKey"], locale: Locale) {
  const labels = {
    courier_dispatch: ["إسناد السائقين", "Dispatch coursiers"],
    customer_ordering: ["طلبات الزبائن", "Commandes clients"],
    customer_tracking: ["التتبع المباشر", "Suivi client"],
    merchant_order_actions: ["إجراءات المطاعم", "Actions commerces"],
    sandbox_card_payments: ["الدفع بالبطاقة", "Paiement carte"],
  } as const;
  return labels[key][locale === "ar" ? 0 : 1];
}
function passkeyDeviceName(): string {
  const platform = navigator.platform?.trim() || "web";
  return `GoBro Operations · ${platform}`.slice(0, 80);
}

function passkeyErrorText(error: unknown, locale: Locale) {
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return locale === "ar"
      ? "أُلغيت مراسم مفتاح المرور أو انتهت مهلتها؛ حاول مجددًا"
      : "Cérémonie passkey annulée ou expirée; réessayez";
  }
  return errorText(error, locale);
}

function errorText(error: unknown, locale: Locale) {
  if (error instanceof ApiError) {
    if (error.status === 401)
      return locale === "ar" ? "انتهت الجلسة؛ أعد تسجيل الدخول" : "Session expirée";
    if (error.status === 403)
      return locale === "ar"
        ? "لا يسمح دورك بهذا الإجراء"
        : "Votre rôle ne permet pas cette action";
    if (error.status === 409)
      return locale === "ar"
        ? "تغيّرت البيانات؛ حدّث الصفحة وأعد المراجعة"
        : "Les données ont changé; actualisez";
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
function previewOrderDetail(order: OperationsOrder, perspective: string): Record<string, unknown> {
  const storeLocation = {
    latitude: order.destination.latitude + 0.006,
    longitude: order.destination.longitude - 0.007,
  };
  return {
    id: order.id,
    publicReference: order.publicReference,
    status: order.status,
    perspective,
    store: { id: order.storeId, location: storeLocation, name: order.storeName },
    pickup: storeLocation,
    courier: {
      location: order.courierName
        ? {
            latitude: order.destination.latitude + 0.003,
            longitude: order.destination.longitude - 0.0035,
          }
        : null,
      name: order.courierName,
    },
    money: { customerTotalMillimes: order.customerTotalMillimes },
    destination: order.destination,
    version: order.version,
  };
}
function previewCaseDetail(item: OperationsCase): CaseDetail {
  return {
    ...item,
    evidence: [
      {
        createdAt: item.updatedAt,
        evidenceHash: "a713f0d1a0cf6a7180a343cb5c4c89455324b27bf638adf9a2854f5c148b22d7",
        evidenceType: "tracking_snapshot",
        id: `${item.id.slice(0, -1)}7`,
        note: "توقف التتبع لسبع دقائق ثم استعاد الاتصال.",
        referenceId: item.orderId,
      },
    ],
    events: [
      {
        createdAt: item.createdAt,
        eventType: "case_created",
        id: `${item.id.slice(0, -1)}8`,
        note: "تم فتح القضية من بلاغ الزبون.",
        performedByUserId: "019f9000-0000-7000-8000-000000000099",
        sequence: 1,
        toStatus: "open",
      },
    ],
    resolutionCode: null,
    summary:
      "تأخر الطلب بعد مغادرة المطعم مع انقطاع مؤقت في موقع السائق. يلزم التحقق من مسار التسليم والتواصل مع الأطراف.",
  };
}
